import { useState } from 'react';
import type { SessionInfo } from '@shared/types';
import { describeUserAgent, formatDateTime, formatRelative, formatRemaining } from '@shared/format';
import { api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, CardHeader, PageHeader } from '../components/ui';
import { Notice } from '../components/form';
import { ConfirmDialog, useToast } from '../components/feedback';
import { Table, type Column } from '../components/table';

/** 会话与设备。会话列表来自服务端落库的 jti，不是前端猜的。 */
export function SessionsPage() {
  const toast = useToast();
  const sessions = useAsync<{ sessions: SessionInfo[] }>(() => api.get('/api/security/sessions'), []);
  const [confirmOthers, setConfirmOthers] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const columns: Column<SessionInfo>[] = [
    {
      key: 'device',
      header: '设备',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium">
            {describeUserAgent(row.userAgent)}
            {row.current ? (
              <span className="ml-2">
                <Badge tone="brand">当前会话</Badge>
              </span>
            ) : null}
          </p>
          <p className="truncate font-mono text-xs text-fg-muted">{row.ip ?? '未知 IP'}</p>
        </div>
      ),
    },
    {
      key: 'lastSeen',
      header: '最近活动',
      render: (row) => <span className="text-fg-muted">{formatRelative(row.lastSeenAt)}</span>,
    },
    {
      key: 'expires',
      header: '有效期',
      render: (row) => <span className="text-fg-muted">{formatRemaining(row.expiresAt)}</span>,
    },
    {
      key: 'createdAt',
      header: '登录时间',
      render: (row) => <span className="text-fg-muted">{formatDateTime(row.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '操作',
      hideOnMobile: true,
      render: (row) => (
        <Button
          size="sm"
          variant="link"
          onClick={async () => {
            setBusyId(row.id);
            try {
              const result = await api.delete<{ sessions: SessionInfo[] }>(
                `/api/security/sessions/${row.id}`,
              );
              sessions.setData(result);
              toast.push(row.current ? '已结束当前会话' : '已结束该设备上的会话', 'ok');
            } catch (error) {
              toast.push(describeError(error), 'danger');
            } finally {
              setBusyId(null);
            }
          }}
        >
          {row.current ? '结束当前' : '结束'}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="会话与设备"
        description="每一行都对应一次真实的登录。发现不认识的设备就结束它。"
        action={
          <Button
            icon="logout"
            onClick={() => setConfirmOthers(true)}
            disabled={(sessions.data?.sessions.length ?? 0) <= 1}
          >
            退出其它设备
          </Button>
        }
      />

      <Card>
        <CardHeader title="登录中的设备" hint="会话到期或被你结束之后就不再出现在这里" />
        {busyId ? <p className="mb-2 text-xs text-fg-subtle">正在处理…</p> : null}
        <Table
          caption="登录中的设备"
          columns={columns}
          rows={sessions.data?.sessions ?? []}
          getKey={(row) => row.id}
          loading={sessions.loading}
          empty={{ title: '没有登录中的设备' }}
        />
      </Card>

      <Notice tone="info">
        会话到期时间在签发时就定死了，不会因为一直使用而无限延长。勾选「记住我」的会话有效期更长，
        因此不要在公共电脑上勾选。
      </Notice>

      <ConfirmDialog
        open={confirmOthers}
        title="退出其它设备？"
        description="当前这台设备会保持登录，其余全部结束。之后需要用口令重新登录。"
        confirmLabel="退出其它设备"
        onCancel={() => setConfirmOthers(false)}
        onConfirm={async () => {
          setConfirmOthers(false);
          try {
            const result = await api.post<{ sessions: SessionInfo[]; revoked: number }>(
              '/api/security/sessions/revoke',
            );
            sessions.setData(result);
            toast.push(`已结束 ${result.revoked} 个会话`, 'ok');
          } catch (error) {
            toast.push(describeError(error), 'danger');
          }
        }}
      />
    </div>
  );
}
